import { useState } from "react"

import coverBg from "@/assets/textures/tournament-welcome.png"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import GiftIcon from "@/assets/icons/gift.svg?react"
import { useI18n } from "@/shared/i18n"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"
import { formatTournamentStartsAt } from "@/shared/lib/formatTournamentStartsAt"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { cn } from "@/shared/lib/utils"
import type {
  PromisedReward,
  Tournament,
  TournamentParticipant,
} from "@/shared/api/types/tournaments"

import { TournamentFlipCountdown } from "./TournamentFlipCountdown"
import { TournamentNominationsHelpButton } from "./TournamentNominationsHelpButton"

function highlightAssetTokens(text: string) {
  return text.split(/(TON|NFT)/g).map((part, i) =>
    part === "TON" || part === "NFT" ? (
      <span key={i} className="text-green-100">
        {part}
      </span>
    ) : (
      part
    )
  )
}

function formatSignedTon(nano: number): string {
  const ton = nano / 1e9
  const sign = ton > 0 ? "+" : ton < 0 ? "−" : ""
  return `${sign}${Math.abs(ton).toFixed(2)}`
}

function PrizeBadge({
  reward,
  className,
  style,
}: {
  reward: PromisedReward
  className?: string
  style?: React.CSSProperties
}) {
  const hasTon = reward.tonNano > 0
  const giftCount = reward.giftNftIds.length
  const hasGift = giftCount > 0
  if (!hasTon && !hasGift) return null

  return (
    <span
      className={cn(
        "flex items-center gap-1.5 rounded-full px-2 py-1.5 text-sm font-semibold leading-4",
        className
      )}
      style={style}
    >
      {hasGift && (
        <span className="flex items-center gap-0.5">
          <GiftIcon className="size-3.5" />
          {giftCount > 1 ? giftCount : null}
        </span>
      )}
      {hasTon && (
        <span className="flex items-center gap-0.5">
          {formatTonFromNano(reward.tonNano)}
          <TonIcon className="size-3.5" />
        </span>
      )}
    </span>
  )
}

const PLACE_THEME: Record<
  1 | 2 | 3,
  { border: string; pill: string; badgeFill: string; gradient: string }
> = {
  1: {
    border: "#F9C23C",
    pill: "#F9C23C",
    badgeFill: "#D3883E",
    gradient: "rgba(249, 194, 60, 0.4)",
  },
  2: {
    border: "#A2E6FF",
    pill: "#A2E6FF",
    badgeFill: "#72929D",
    gradient: "rgba(162, 230, 255, 0.4)",
  },
  3: {
    border: "#934C00",
    pill: "#934C00",
    badgeFill: "#D3883E",
    gradient: "rgba(147, 76, 0, 0.4)",
  },
}

function Avatar({
  url,
  name,
  className,
  style,
}: {
  url: string | null
  name: string | null
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary-main font-semibold text-white",
        className
      )}
      style={style}
    >
      {url ? (
        <img
          src={resolveAvatarLoadUrl(url)}
          alt=""
          className="absolute inset-0 block size-full rounded-full object-cover"
        />
      ) : (
        getAvatarInitials(name, "?", 1)
      )}
    </div>
  )
}

function PodiumPlace({
  place,
  participant,
}: {
  place: 1 | 2 | 3
  participant: TournamentParticipant
}) {
  const theme = PLACE_THEME[place]
  const isFirst = place === 1
  const avatarSize = isFirst ? 64 : 56

  return (
    <div className="flex min-w-0 flex-1 basis-0 flex-col items-center">
      <div
        className="relative z-10 flex flex-col items-center"
        style={{ marginBottom: -avatarSize / 2 }}
      >
        <div
          className="relative"
          style={{ width: avatarSize, height: avatarSize }}
        >
          <Avatar
            url={participant.photoUrl}
            name={participant.username}
            className="size-full border-2 text-lg"
            style={{ borderColor: theme.border }}
          />
          <span
            className="absolute bottom-0 left-1/2 z-10 flex size-6 -translate-x-1/2 translate-y-[10px] items-center justify-center rounded-full border-2 text-xs font-bold text-white"
            style={{ background: theme.badgeFill, borderColor: theme.border }}
          >
            {place}
          </span>
        </div>
      </div>

      <div
        className={cn(
          "relative flex w-full flex-col items-center rounded-t-3xl px-1 pb-3",
          isFirst ? "h-40" : "h-32"
        )}
        style={{
          paddingTop: avatarSize / 2 + 15.5,
          background: `linear-gradient(180deg, ${theme.gradient} 0%, rgba(0,0,0,0) 100%)`,
        }}
      >
        <div className="flex w-full flex-col items-center gap-[4px]">
          <span className="w-full truncate text-center text-base font-medium text-white">
            {participant.username ||
              getAvatarInitials(participant.username, "?", 1)}
          </span>
          <span className="flex max-w-full items-center gap-0.5 truncate text-xs font-semibold leading-4 text-white/60">
            {formatSignedTon(participant.netScoreNanoTon)}
            <TonIcon className="size-3.5 shrink-0" />
          </span>
        </div>

        <div className="absolute bottom-0 left-1/2 z-10 flex -translate-x-1/2 translate-y-1/2 items-center">
          <PrizeBadge
            reward={participant.promisedReward}
            className="text-black-100"
            style={{ background: theme.pill }}
          />
        </div>
      </div>
    </div>
  )
}

const TOTAL_LEADERBOARD_PLACES = 50

function getPromisedRewardForPlace(
  tournament: Tournament,
  place: number
): PromisedReward {
  const promise = tournament.rewardPromises.find((p) => p.place === place)
  if (!promise) return { tonNano: 0, giftNftIds: [] }
  return {
    tonNano: Math.floor(
      (tournament.currentPoolNanoTon * promise.tonPoolBps) / 10000
    ),
    giftNftIds: promise.giftNftIds,
  }
}

function PlaceholderRow({
  place,
  reward,
  waitingLabel,
}: {
  place: number
  reward: PromisedReward
  waitingLabel: string
}) {
  return (
    <div className="flex items-center gap-2 rounded-2xl bg-secondary-main p-3">
      <span className="flex size-6 shrink-0 items-center justify-center text-xs font-bold text-white">
        {place}
      </span>
      <span
        aria-hidden
        className="size-9 shrink-0 rounded-full bg-tertiary-main"
      />
      <span className="min-w-0 flex-1 truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
        {waitingLabel}
      </span>
      <PrizeBadge
        reward={reward}
        className="shrink-0 bg-tertiary-main text-white"
      />
    </div>
  )
}

function LeaderboardRow({
  participant,
  isMe,
}: {
  participant: TournamentParticipant
  isMe: boolean
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-2xl bg-secondary-main p-3",
        isMe && "ring-1 ring-green-100"
      )}
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
        {participant.rank}
      </span>
      <Avatar
        url={participant.photoUrl}
        name={participant.username}
        className="size-9 shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
          {participant.username ||
            getAvatarInitials(participant.username, "?", 1)}
        </span>
        <span className="flex items-center gap-0.5 text-xs font-semibold leading-4 tracking-[-0.5px] text-[#19AF2A]">
          {formatSignedTon(participant.netScoreNanoTon)}
          <TonIcon className="size-3.5 -translate-y-px" />
        </span>
      </div>
      <PrizeBadge
        reward={participant.promisedReward}
        className="shrink-0 bg-tertiary-main text-white"
      />
    </div>
  )
}

interface TournamentLeaderboardProps {
  tournament: Tournament
  leaderboard: TournamentParticipant[]
  myUserId?: string | null
  className?: string
}

export function TournamentLeaderboard({
  tournament,
  leaderboard,
  myUserId,
  className,
}: TournamentLeaderboardProps) {
  const { t, locale } = useI18n()
  const [expanded, setExpanded] = useState(false)
  const now = Date.now()
  const isUpcoming = tournament.isUpcoming || now < tournament.startsAt
  const isEnded =
    tournament.isFinished || (now >= tournament.endsAt && tournament.endsAt > 0)
  const coverTarget = isUpcoming ? tournament.startsAt : tournament.endsAt
  const coverLabel = isEnded
    ? t("tournamentLeaderboard.ended")
    : isUpcoming
      ? t("tournamentLeaderboard.startsIn")
      : t("tournamentLeaderboard.endsIn")
  const pool = formatTonFromNano(tournament.currentPoolNanoTon, {
    allowZero: true,
  })

  const participantByRank = new Map(leaderboard.map((p) => [p.rank, p]))
  const podiumOrder: Array<{
    place: 1 | 2 | 3
    participant?: TournamentParticipant
  }> = [
    { place: 2, participant: participantByRank.get(2) },
    { place: 1, participant: participantByRank.get(1) },
    { place: 3, participant: participantByRank.get(3) },
  ]
  const restSlots: Array<{
    place: number
    participant?: TournamentParticipant
  }> = []
  for (let place = 4; place <= TOTAL_LEADERBOARD_PLACES; place++) {
    restSlots.push({ place, participant: participantByRank.get(place) })
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="relative -mx-4 overflow-hidden pb-2 pt-6">
        <div
          aria-hidden
          className="pointer-events-none absolute opacity-60"
          style={{
            width: 390,
            height: 229,
            right: 0,
            top: -11,
            backgroundImage: `url(${coverBg})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right top",
            maskImage:
              "radial-gradient(57% 52% at 72% 40%, #000 7%, transparent 64%)",
            WebkitMaskImage:
              "radial-gradient(57% 52% at 72% 40%, #000 7%, transparent 64%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #201330 16%, rgba(32,19,48,0) 72%), linear-gradient(180deg, rgba(32,19,48,0) 50%, #201330 100%)",
          }}
        />
        <div className="relative flex flex-col gap-3 px-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase leading-3 tracking-[-0.5px] text-green-100">
              {coverLabel}
            </span>
            <div className="flex items-start justify-between gap-2">
              <TournamentFlipCountdown to={coverTarget} variant="xs" />
              <TournamentNominationsHelpButton />
            </div>

            {isUpcoming && (
              <span className="text-xs font-medium leading-4 tracking-[-0.5px] text-white/70">
                {t("tournamentLeaderboard.startsAt", {
                  date: formatTournamentStartsAt(tournament.startsAt, locale),
                })}
              </span>
            )}
          </div>
          {!isUpcoming && (
            <div className="flex flex-wrap items-center gap-x-1.5 text-2xl font-semibold tracking-[-0.5px] text-white">
              <span className="flex items-center gap-1">
                {pool}
                <TonIcon className="size-6" />
              </span>
              <span>{t("tournamentLeaderboard.prizePool")}</span>
            </div>
          )}
          <p
            className={cn(
              "text-base leading-[1.3] tracking-[-0.5px] text-white",
              !expanded && "line-clamp-2"
            )}
          >
            {highlightAssetTokens(t("tournamentLeaderboard.rules"))}
          </p>
          {expanded && (
            <div className="flex flex-col gap-2">
              <h3 className="text-lg font-semibold leading-[normal] tracking-[-0.5px] text-white">
                {t("tournamentLeaderboard.rulesTitle")}
              </h3>
              <ul className="ml-6 list-disc space-y-2 text-base leading-[1.3] tracking-[-0.5px] text-white marker:text-white">
                {[
                  t("tournamentLeaderboard.rule1"),
                  t("tournamentLeaderboard.rule2"),
                  t("tournamentLeaderboard.rule3"),
                  t("tournamentLeaderboard.rule4"),
                  t("tournamentLeaderboard.rule5"),
                ].map((rule, i) => (
                  <li key={i}>{rule}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="self-start rounded-full bg-secondary-main px-3 py-1.5 text-sm font-medium tracking-[-0.5px] text-white transition-opacity active:opacity-70"
          >
            {expanded
              ? t("tournamentLeaderboard.showLess")
              : t("tournamentLeaderboard.showMore")}
          </button>
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <p className="py-10 text-center text-sm text-white/60">
          {t("tournamentLeaderboard.empty")}
        </p>
      ) : (
        <>
          <div className="flex items-end gap-1 pb-[14px]">
            {podiumOrder.map(({ place, participant }) =>
              participant ? (
                <PodiumPlace
                  key={place}
                  place={place}
                  participant={participant}
                />
              ) : (
                <div key={place} className="flex-1 basis-0" />
              )
            )}
          </div>

          <div className="flex flex-col gap-2">
            {restSlots.map(({ place, participant }) =>
              participant ? (
                <LeaderboardRow
                  key={participant.userId}
                  participant={participant}
                  isMe={!!myUserId && participant.userId === myUserId}
                />
              ) : (
                <PlaceholderRow
                  key={`placeholder-${place}`}
                  place={place}
                  reward={getPromisedRewardForPlace(tournament, place)}
                  waitingLabel={t("tournamentLeaderboard.waiting")}
                />
              )
            )}
          </div>
        </>
      )}
    </div>
  )
}
